/** Flags fields a pack or bundle carries that its format does not define, so a load can ignore them and say so. Pure, shared by server and client. */

export type PackKind = 'runtimeBundle' | 'assessmentPack' | 'evaluationPack';

export type PackFieldWarningKind = 'misspelled' | 'unknown';

/** The cross-repo warning shape; `path` is a JSON pointer to the ignored key. */
export interface PackFieldWarning {
  path: string;
  field: string;
  kind: PackFieldWarningKind;
  suggestion: string | null;
}

/** The warnings for one pack a load read; `source` names a recorded bundle when there are several. */
export interface PackFieldReport {
  pack: PackKind;
  source?: string;
  warnings: PackFieldWarning[];
}

/** Each known field maps to the shape of its object, or of each object in its array; null when its value has no fields of its own. */
export interface FieldShape {
  readonly [field: string]: FieldShape | null;
}

export const MAX_SUGGESTION_DISTANCE = 2;

// The runtime's Bundle schema (asenion-policy-runtime runtime/src/bundle.rs).
const FRAMEWORK_REF: FieldShape = {
  source: null,
  citation_text: null,
  url: null,
};

export const RUNTIME_POLICY_FIELDS: FieldShape = {
  identifier: null,
  title: null,
  severity: null,
  framework_refs: FRAMEWORK_REF,
  triggers: {
    action_class_in: null,
    action_class_scope: null,
    intent_matches: null,
    topic_any_of: null,
    topic_required_all_of: null,
    requires_prior_topic: null,
  },
  guidance: {
    kind: null,
    summary: null,
    recommended_action: null,
    suggested_response_template: null,
    escalation: { channel: null, notify: null },
  },
  audit_obligations: { control_ref: null, obligation: null },
  evaluation: { compliant_response_shape: null, failure_modes: null },
};

const RUNTIME_BUNDLE_FIELDS: FieldShape = {
  bundle: {
    identifier: null,
    version: null,
    source_effective_date: null,
    last_reviewed_date: null,
    authored_by: null,
    reviewed_by: null,
    description: null,
    framework_mappings: { framework: null, url: null, mapped_items: null },
    labels: null,
    policies: RUNTIME_POLICY_FIELDS,
    schema_version: null,
    generator: { name: null, version: null, run_id: null },
    receipt_bearing_action_classes: null,
  },
  // The sibling fairly-app writes beside `bundle` in a composed scope bundle.
  sources: {
    control_id: null,
    policy_id: null,
    policy_identifier: null,
    policy_version: null,
    attached_to_project_id: null,
    attached_to_project_type: null,
    level: null,
    source_sha256: null,
  },
};

// The engine's assessment renderer; alert and risk entries follow the platform's answer-option schema.
const ANSWER_OPTION: FieldShape = {
  identifier: null,
  type: null,
  value: null,
  answer: null,
  description: null,
  stateConclusion: null,
  alert: {
    alertName: null,
    alertCategory: null,
    alertText: null,
    frameworkLevel: null,
    alertID: null,
  },
  risk: { riskType: null, riskExplanation: null },
};

const ASSESSMENT_PACK_FIELDS: FieldShape = {
  organization: { name: null, identifier: null, pgId: null },
  policies: {
    name: null,
    identifier: null,
    version: null,
    description: null,
    link: null,
    labels: null,
    controlBundles: { identifier: null, version: null },
  },
  controlBundles: {
    identifier: null,
    name: null,
    description: null,
    version: null,
    weight: null,
    labels: null,
    source: null,
    controls: {
      identifier: null,
      name: null,
      description: null,
      controlCitations: {
        citationSource: null,
        citationTitle: null,
        citationText: null,
      },
      question: null,
      answerType: null,
      answerOptions: ANSWER_OPTION,
      cardTypes: null,
      frameworkLevel: null,
      technicalArea: null,
      industry: null,
      prerequisiteState: null,
      controlConclusion: null,
      role: null,
      usecase: null,
    },
  },
};

// The engine's GET /runs/:id/pack/evaluation.
const EVALUATION_PACK_FIELDS: FieldShape = {
  evaluation_pack_version: null,
  run_id: null,
  document_id: null,
  control_count: null,
  controls: {
    control_id: null,
    control_version: null,
    rubric_dimensions: {
      dimension_id: null,
      required_behavior: null,
      forbidden_behavior: null,
      citation_requirement: null,
      uncertainty_handling: null,
      weight: null,
      failure_modes: null,
    },
    seed_catalog: {
      seed_id: null,
      prompt: null,
      expected_guidance_kind: null,
      difficulty: null,
      source_anchor_ref: null,
      attack_category: null,
      scenario_type: null,
      attack_intent: null,
    },
    signal_hints_sidecar: {
      control_id: null,
      positive: null,
      negative: null,
      neutral: null,
    },
  },
};

export const PACK_FIELDS: Record<PackKind, FieldShape> = {
  runtimeBundle: RUNTIME_BUNDLE_FIELDS,
  assessmentPack: ASSESSMENT_PACK_FIELDS,
  evaluationPack: EVALUATION_PACK_FIELDS,
};

export const PACK_LABEL: Record<PackKind, string> = {
  runtimeBundle: 'runtime bundle',
  assessmentPack: 'assessment pack',
  evaluationPack: 'evaluation pack',
};

/** Unrestricted Damerau-Levenshtein (adjacent transpositions allowed across edits), the same measure as strsim's damerau_levenshtein. */
export function damerauLevenshtein(a: string, b: string): number {
  const s = Array.from(a);
  const t = Array.from(b);
  const max = s.length + t.length;
  const d: number[][] = Array.from({ length: s.length + 2 }, () =>
    new Array<number>(t.length + 2).fill(0)
  );
  d[0][0] = max;
  for (let i = 0; i <= s.length; i += 1) {
    d[i + 1][0] = max;
    d[i + 1][1] = i;
  }
  for (let j = 0; j <= t.length; j += 1) {
    d[0][j + 1] = max;
    d[1][j + 1] = j;
  }
  const lastRowOf = new Map<string, number>();
  for (let i = 1; i <= s.length; i += 1) {
    let lastMatchCol = 0;
    for (let j = 1; j <= t.length; j += 1) {
      const k = lastRowOf.get(t[j - 1]) ?? 0;
      const l = lastMatchCol;
      let cost = 1;
      if (s[i - 1] === t[j - 1]) {
        cost = 0;
        lastMatchCol = j;
      }
      d[i + 1][j + 1] = Math.min(
        d[i][j] + cost,
        d[i + 1][j] + 1,
        d[i][j + 1] + 1,
        d[k][l] + (i - k - 1) + 1 + (j - l - 1)
      );
    }
    lastRowOf.set(s[i - 1], i);
  }
  return d[s.length + 1][t.length + 1];
}

/** The one known field within distance 2 of `key`, compared case-insensitively; null when none or several are. */
export function suggestionFor(
  key: string,
  known: readonly string[]
): string | null {
  const lower = key.toLowerCase();
  const near = known.filter(
    (field) =>
      damerauLevenshtein(lower, field.toLowerCase()) <= MAX_SUGGESTION_DISTANCE
  );
  return near.length === 1 ? near[0] : null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

const hasField = (shape: FieldShape, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(shape, key);

const pointerSegment = (key: string): string =>
  key.replace(/~/g, '~0').replace(/\//g, '~1');

function collect(
  value: unknown,
  shape: FieldShape,
  path: string,
  out: PackFieldWarning[]
): void {
  if (Array.isArray(value)) {
    value.forEach((entry, index) =>
      collect(entry, shape, `${path}/${index}`, out)
    );
    return;
  }
  if (!isPlainObject(value)) {
    return;
  }
  Object.keys(value).forEach((key) => {
    const at = `${path}/${pointerSegment(key)}`;
    if (hasField(shape, key)) {
      const child = shape[key];
      if (child) {
        collect(value[key], child, at, out);
      }
      return;
    }
    const suggestion = suggestionFor(key, Object.keys(shape));
    out.push({
      path: at,
      field: key,
      kind: suggestion ? 'misspelled' : 'unknown',
      suggestion,
    });
  });
}

/** Every key in `value` its shape does not define, in document order. Never throws on a value of the wrong type. */
export function checkFields(
  value: unknown,
  shape: FieldShape,
  basePath = ''
): PackFieldWarning[] {
  const out: PackFieldWarning[] = [];
  collect(value, shape, basePath, out);
  return out;
}

export function checkPackFields(
  pack: PackKind,
  doc: unknown
): PackFieldWarning[] {
  return checkFields(doc, PACK_FIELDS[pack]);
}

/** A copy of `value` without the keys `checkFields` flags. Values with no shape of their own are kept as they are. */
export function withoutUnknownFields<T>(value: T, shape: FieldShape): T {
  if (Array.isArray(value)) {
    return value.map((entry) =>
      withoutUnknownFields(entry, shape)
    ) as unknown as T;
  }
  if (!isPlainObject(value)) {
    return value;
  }
  const out: Record<string, unknown> = {};
  Object.keys(value).forEach((key) => {
    if (!hasField(shape, key)) {
      return;
    }
    const child = shape[key];
    out[key] = child ? withoutUnknownFields(value[key], child) : value[key];
  });
  return out as T;
}

export function withoutUnknownPackFields<T>(pack: PackKind, doc: T): T {
  return withoutUnknownFields(doc, PACK_FIELDS[pack]);
}

const SINGULAR: Record<string, string> = {
  policies: 'policy',
  controls: 'control',
  controlBundles: 'control bundle',
  answerOptions: 'answer option',
  controlCitations: 'citation',
  framework_refs: 'framework reference',
  framework_mappings: 'framework mapping',
  rubric_dimensions: 'rubric dimension',
  seed_catalog: 'seed',
  sources: 'source',
};

const words = (segment: string): string =>
  segment
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();

const singular = (segment: string): string =>
  SINGULAR[segment] ?? words(segment).replace(/ies$/, 'y').replace(/s$/, '');

const unescapeSegment = (segment: string): string =>
  segment.replace(/~1/g, '/').replace(/~0/g, '~');

/** Where a warning's key sat, in words: "in policy 4", "in control bundle 1, control 3's answer options", or "at the top level". */
export function describeLocation(path: string): string {
  const segments = path.split('/').slice(1, -1).map(unescapeSegment);
  // The runtime bundle's wrapper key says nothing a reader needs.
  if (segments[0] === 'bundle') {
    segments.shift();
  }
  const indexed: string[] = [];
  let trailing: string[] = [];
  for (let i = 0; i < segments.length; i += 1) {
    const next = segments[i + 1];
    if (next !== undefined && /^\d+$/.test(next)) {
      indexed.push(`${singular(segments[i])} ${Number(next) + 1}`);
      trailing = [];
      i += 1;
    } else if (!/^\d+$/.test(segments[i])) {
      trailing.push(words(segments[i]));
    }
  }
  if (indexed.length === 0) {
    return trailing.length === 0
      ? 'at the top level'
      : `in the ${trailing.join("'s ")}`;
  }
  const base = indexed.join(', ');
  return trailing.length === 0
    ? `in ${base}`
    : `in ${base}'s ${trailing.join("'s ")}`;
}

/** A report as it arrives over the wire, where GraphQL types the enums as strings. */
export interface PackFieldReportLike {
  pack: string;
  source?: string | null;
  warnings: readonly {
    path: string;
    field: string;
    kind: string;
    suggestion?: string | null;
  }[];
}

const labelOf = (pack: string): string =>
  (PACK_LABEL as Record<string, string>)[pack] ?? pack;

/** One warning as a sentence for a person, e.g. `Unknown field "guidence" in policy 4 — did you mean "guidance"? It was ignored.` */
export function describePackFieldWarning(
  warning: PackFieldReportLike['warnings'][number],
  pack?: string
): string {
  const where = describeLocation(warning.path);
  const of = pack ? ` of the ${labelOf(pack)}` : '';
  const head = `Unknown field "${warning.field}" ${where}${of}`;
  return warning.kind === 'misspelled' && warning.suggestion
    ? `${head} — did you mean "${warning.suggestion}"? It was ignored.`
    : `${head}. It was ignored.`;
}

/** Every warning across reports as a keyed sentence, prefixed by its recorded source when it has one. */
export function packFieldWarningLines(
  reports: readonly PackFieldReportLike[] | null | undefined
): { key: string; text: string }[] {
  return (reports ?? []).flatMap((report) =>
    report.warnings.map((warning) => {
      const sentence = describePackFieldWarning(warning, report.pack);
      return {
        key: `${report.pack}|${report.source ?? ''}|${warning.path}`,
        text: report.source ? `${report.source}: ${sentence}` : sentence,
      };
    })
  );
}

/** The report for one pack as a list: empty when the pack is clean, so callers can spread it. */
export function reportPackFields(
  pack: PackKind,
  doc: unknown,
  source?: string
): PackFieldReport[] {
  const warnings = checkPackFields(pack, doc);
  if (warnings.length === 0) {
    return [];
  }
  return [source ? { pack, source, warnings } : { pack, warnings }];
}

export const countPackFieldWarnings = (
  reports: readonly PackFieldReportLike[] | null | undefined
): number => (reports ?? []).reduce((n, r) => n + r.warnings.length, 0);
